import React, { useState, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';
const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const TYPES = ['Hospital','Clinic','Doctor','GP','Dentist','Pharmacy','Spa','Wellness','Gym','Fitness','Yoga','Hair Salon','Nail Salon','Barbershop','Massage','Hotel & Resort','Mental Health','Physiotherapy','Veterinary','Optical'];
function useIsMobile() {
  const [m,setM]=React.useState(()=>typeof window!=='undefined'&&window.innerWidth<768);
  React.useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  return m;
}
function BulkEditorBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [filters,setFilters]=useState({country:'',type:'',city:'',missingCoords:false,missingPhone:false,missingWebsite:false});
  const [rows,setRows]=useState([]);
  const [selected,setSelected]=useState(new Set());
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [bulk,setBulk]=useState({type:'',tags:'',country:''});
  const [confirm,setConfirm]=useState(false);

  const search = useCallback(async () => {
    setLoading(true); setSelected(new Set()); setRows([]);
    let q = supabase.from('Locations').select('id,name,type,city,country,phone,website,latitude,tags').limit(100);
    if (filters.country) q = q.ilike('country', `*${filters.country}*`);
    if (filters.type)    q = q.ilike('type', `*${filters.type}*`);
    if (filters.city)    q = q.ilike('city', `*${filters.city}*`);
    if (filters.missingCoords)   q = q.is('latitude', null);
    if (filters.missingPhone)    q = q.is('phone', null);
    if (filters.missingWebsite)  q = q.is('website', null);
    const { data, error } = await q;
    if (error) setMsg('Error: ' + error.message);
    else setRows(data || []);
    setLoading(false);
  }, [filters]);

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const applyBulk = async () => {
    if (!selected.size) return;
    setSaving(true); setConfirm(false);
    const updates = {};
    if (bulk.type)    updates.type = bulk.type;
    if (bulk.tags)    updates.tags = bulk.tags;
    if (bulk.country) updates.country = bulk.country;
    if (!Object.keys(updates).length) { setSaving(false); setMsg('No fields to update.'); return; }
    const ids = [...selected];
    const { error } = await supabase.from('Locations').update(updates).in('id', ids);
    if (error) setMsg('Error: ' + error.message);
    else { setMsg(`✓ Updated ${ids.length} rows.`); setRows(prev => prev.map(r => selected.has(r.id) ? {...r,...updates} : r)); setSelected(new Set()); setBulk({type:'',tags:'',country:''}); }
    setSaving(false);
  };

  const S = { th: { fontSize:'.65rem', color:'var(--color-text-secondary)', textTransform:'uppercase', letterSpacing:'.04em', padding:'6px 8px', textAlign:'left', borderBottom:'1px solid var(--color-border-tertiary)', fontWeight:500 }, td: { fontSize:'.72rem', padding:'6px 8px', borderBottom:'.5px solid var(--color-border-tertiary)', color:'var(--color-text-primary)' } };

  return (
    <div style={{fontFamily:F,padding:isMobile?'16px 14px 80px':'24px 28px'}}>
      <div style={{marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:500,margin:0,color:'var(--color-text-primary)'}}>Bulk location editor</h2>
        <p style={{fontSize:'.78rem',color:'var(--color-text-secondary)',margin:'4px 0 0'}}>Search, multi-select, and mass-update type, tags, or country across any set of locations.</p>
      </div>
      <Card style={{marginBottom:16}}>
        <CardTitle>Search filters</CardTitle>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8,marginBottom:10}}>
          {[['Country','country'],['City','city'],['Type','type']].map(([pl,key])=>(
            <input key={key} value={filters[key]} onChange={e=>setFilters(f=>({...f,[key]:e.target.value}))} placeholder={pl} onKeyDown={e=>e.key==='Enter'&&search()}
              style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--color-border-secondary)',background:'var(--color-background-secondary)',color:'var(--color-text-primary)',fontSize:'.78rem',fontFamily:F}}/>
          ))}
        </div>
        <div style={{display:'flex',gap:14,marginBottom:10,flexWrap:'wrap'}}>
          {[['missingCoords','Missing GPS'],['missingPhone','Missing phone'],['missingWebsite','Missing website']].map(([k,l])=>(
            <label key={k} style={{display:'flex',alignItems:'center',gap:5,fontSize:'.75rem',color:'var(--color-text-secondary)',cursor:'pointer'}}>
              <input type="checkbox" checked={filters[k]} onChange={e=>setFilters(f=>({...f,[k]:e.target.checked}))} />
              {l}
            </label>
          ))}
        </div>
        <button onClick={search} disabled={loading} style={{padding:'7px 18px',borderRadius:8,background:t.accent,color:'#AFE1F6',border:'none',fontSize:'.78rem',fontWeight:500,cursor:'pointer',fontFamily:F}}>
          {loading?'Searching…':'Search'}
        </button>
      </Card>
      {rows.length>0&&(
        <>
          <Card style={{marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10,flexWrap:'wrap',gap:8}}>
              <CardTitle style={{marginBottom:0}}>{rows.length} results — {selected.size} selected</CardTitle>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <button onClick={toggleAll} style={{padding:'4px 10px',borderRadius:6,border:'1px solid var(--color-border-secondary)',background:'none',color:'var(--color-text-secondary)',fontSize:'.72rem',cursor:'pointer',fontFamily:F}}>
                  {selected.size===rows.length?'Deselect all':'Select all'}
                </button>
                {selected.size>0&&<span style={{padding:'4px 10px',borderRadius:6,background:'#E1F5EE',color:'#085041',fontSize:'.72rem',fontWeight:500}}>{selected.size} selected</span>}
              </div>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',minWidth:480}}>
                <thead><tr>
                  <th style={{...S.th,width:36}}><input type="checkbox" checked={selected.size===rows.length&&rows.length>0} onChange={toggleAll}/></th>
                  <th style={S.th}>Name</th><th style={S.th}>Type</th><th style={S.th}>City</th><th style={S.th}>Country</th><th style={S.th}>GPS</th>
                </tr></thead>
                <tbody>{rows.map(r=>(
                  <tr key={r.id} style={{background:selected.has(r.id)?'rgba(49,79,92,0.06)':''}}>
                    <td style={S.td}><input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggle(r.id)}/></td>
                    <td style={{...S.td,fontWeight:500,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.name}</td>
                    <td style={S.td}>{r.type||'—'}</td>
                    <td style={S.td}>{r.city||'—'}</td>
                    <td style={S.td}>{r.country||'—'}</td>
                    <td style={S.td}>{r.latitude?<span style={{color:'#28a745'}}>✓</span>:<span style={{color:'#dc3545'}}>✗</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
          {selected.size>0&&(
            <Card style={{marginBottom:12}}>
              <CardTitle>Bulk update {selected.size} selected rows</CardTitle>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:8,marginBottom:10}}>
                <select value={bulk.type} onChange={e=>setBulk(b=>({...b,type:e.target.value}))} style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--color-border-secondary)',background:'var(--color-background-secondary)',color:'var(--color-text-primary)',fontSize:'.78rem',fontFamily:F}}>
                  <option value="">Set type…</option>
                  {TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
                <input value={bulk.tags} onChange={e=>setBulk(b=>({...b,tags:e.target.value}))} placeholder="Set tags (comma-separated)…"
                  style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--color-border-secondary)',background:'var(--color-background-secondary)',color:'var(--color-text-primary)',fontSize:'.78rem',fontFamily:F}}/>
                <input value={bulk.country} onChange={e=>setBulk(b=>({...b,country:e.target.value}))} placeholder="Set country…"
                  style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--color-border-secondary)',background:'var(--color-background-secondary)',color:'var(--color-text-primary)',fontSize:'.78rem',fontFamily:F}}/>
              </div>
              {!confirm
                ? <button onClick={()=>setConfirm(true)} disabled={!bulk.type&&!bulk.tags&&!bulk.country}
                    style={{padding:'7px 18px',borderRadius:8,background:t.accent,color:'#AFE1F6',border:'none',fontSize:'.78rem',fontWeight:500,cursor:'pointer',fontFamily:F}}>
                    Preview update
                  </button>
                : <div style={{background:'#FAEEDA',borderRadius:8,padding:'10px 14px',border:'1px solid #BA7517'}}>
                    <div style={{fontSize:'.75rem',fontWeight:500,color:'#412402',marginBottom:4}}>Confirm: update {selected.size} rows?</div>
                    <div style={{fontSize:'.72rem',color:'#633806',marginBottom:8}}>
                      {bulk.type&&<div>Set type → <strong>{bulk.type}</strong></div>}
                      {bulk.tags&&<div>Set tags → <strong>{bulk.tags}</strong></div>}
                      {bulk.country&&<div>Set country → <strong>{bulk.country}</strong></div>}
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={applyBulk} disabled={saving} style={{padding:'5px 14px',borderRadius:6,background:t.accent,color:'#AFE1F6',border:'none',fontSize:'.72rem',cursor:'pointer',fontFamily:F,fontWeight:500}}>{saving?'Updating…':'Confirm'}</button>
                      <button onClick={()=>setConfirm(false)} style={{padding:'5px 14px',borderRadius:6,background:'none',color:'#633806',border:'1px solid #BA7517',fontSize:'.72rem',cursor:'pointer',fontFamily:F}}>Cancel</button>
                    </div>
                  </div>
              }
            </Card>
          )}
        </>
      )}
      {rows.length>0&&(
        <div style={{marginTop:8,display:'flex',justifyContent:'flex-end'}}>
          <button onClick={()=>{
            const cols=['id','name','type','city','country','phone','website','latitude','tags'];
            const csv=[cols.join(','),...rows.map(r=>cols.map(c=>`"${(r[c]||'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
            const a=document.createElement('a');a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);a.download='locations.csv';a.click();
          }} style={{fontSize:'.72rem',color:t.mut,background:'none',border:'1px solid var(--color-border-secondary)',borderRadius:6,padding:'4px 10px',cursor:'pointer',fontFamily:F}}>
            ↓ Export CSV ({rows.length} rows)
          </button>
        </div>
      )}
            {msg&&<Insight type={msg.startsWith('✓')?'ok':'warn'} style={{marginTop:8}}>{msg}</Insight>}
    </div>
  );
}

export default function BulkEditor(props) {
  if (props && props.embed) return <BulkEditorBody {...props} />;
  return <ConsoleShell title="Bulk Editor"><BulkEditorBody {...props} /></ConsoleShell>;
}
